<svelte:options
  customElement={{
    tag: "oc-checkbox-v2",
    extend: window.__components.extend(),
    props: {
      labelPlacement: { type: "String", attribute: "label-placement" },
      widthBehavior: { type: "String", attribute: "width-behavior" },
    },
  }}
/>

<script lang="ts">
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import type { Props } from "./CheckboxV2.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import ActionSlot from "../../../common/components/action/ActionSlot.svelte";
  import HintV2 from "../../../common/components/form/HintV2.svelte";
  import type { ActionState } from "../../../common/components/action/ActionSlotProps";
  import ErrorV2 from "../../../common/components/form/ErrorV2.svelte";
  import { useSlots } from "../../../common/utils/useSlots.svelte";

  let { widthBehavior = "fill", labelPlacement = "right" }: Props = $props();

  const Host = $host();

  const slots = useSlots(Host);

  let actionState = $state<ActionState>({});
</script>

<div
  class="checkbox"
  class:--invalid={actionState.invalid}
  class:--disabled={actionState.disabled}
  class:--checked={actionState.checked}
  class:--active={actionState.active}
  class:--hover={actionState.hover}
  class:--label-left={labelPlacement === "left"}
>
  <ActionSlot {Host} bind:actionState />
  <div class="icon"></div>
  {#if slots.label}
    <span class="__label">
      <slot name="label" />
      <FormLabelIconV1 variant={actionState.invalid ? "error" : "default"} />
    </span>
  {/if}
</div>

<ErrorV2 />
<HintV2 disabled={actionState.disabled} />
<DefaultStyles {widthBehavior} />

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;

  :global {
    :host {
      display: block;
      font: tokens.$oc-component-form-field-input-font;
    }

    .checkbox {
      --icon-outline-width: 1px;
      --icon-outline-color: #{tokens.$oc-component-form-default-border-color};
      --icon-background-color: #{tokens.$oc-component-form-background-color};

      display: flex;
      position: relative;
      gap: tokens.$oc-component-form-checkbox-100-gap-x;
      color: tokens.$oc-component-form-default-label-color;
      pointer-events: none; /*                    */
    }

    .--label-left {
      flex-direction: row-reverse;
    }

    .__label {
      flex: 1;
      min-width: 0;
    }

    .icon {
      display: block;
      position: relative;
      width: tokens.$oc-component-form-checkbox-100-container-size;
      height: tokens.$oc-component-form-checkbox-100-container-size;

      outline: var(--icon-outline-width) solid var(--icon-outline-color);
      border-radius: tokens.$oc-component-form-checkbox-100-border-radius;

      background-color: var(--icon-background-color);
      /*                           */
      box-shadow: 0 0 0 1px var(--icon-background-color);

      /*         */
      &:before {
        $center-icon: calc(50% - #{tokens.$oc-component-form-checkbox-100-icon-size} / 2);
        content: "";
        display: block;
        position: absolute;
        inset: $center-icon;
        border-radius: 50%;
        mask-image: url(#{"/assets-static/icons/pl_icon_check50.svg"});
        background-color: tokens.$oc-component-form-default-icon-color-checked;
        visibility: hidden;
      }
    }

    /*                   */
    .--checked {
      --icon-background-color: #{tokens.$oc-component-form-background-color-checked};
      .icon:before {
        visibility: visible; /*                                      */
      }
    }

    /*                 */
    .--hover {
      --icon-outline-width: 2px;
    }

    /*                  */
    .--active {
      --icon-outline-width: 2px;
      --icon-outline-color: #{tokens.$oc-component-form-default-border-color-active};
    }

    /*                   */
    .--invalid {
      color: tokens.$oc-component-form-error-label-color;
      --icon-outline-color: #{tokens.$oc-component-form-error-border-color};

      &.--checked {
        --icon-background-color: #{tokens.$oc-component-form-error-background-color-checked};
      }
    }

    /*                    */
    .checkbox.--disabled {
      color: tokens.$oc-component-form-disabled-label-color;
      --icon-outline-width: 1px;
      --icon-outline-color: #{tokens.$oc-component-form-disabled-border-color};
      --icon-background-color: #{tokens.$oc-component-form-disabled-background-color};
    }
  }
</style>
